import React, { createContext, useContext, useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { authService } from '../services';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Check for existing session on app load
  useEffect(() => {
    console.log('🔐 AuthContext initializing...');
    const savedUser = localStorage.getItem('user');
    console.log('🔐 Saved user from localStorage:', savedUser);
    if (savedUser) {
      try {
        const parsedUser = JSON.parse(savedUser);
        console.log('🔐 Parsed user data:', parsedUser);
        setUser(parsedUser);
      } catch (error) {
        console.error('🔐 Error parsing saved user:', error);
        localStorage.removeItem('user');
      }
    }
    setLoading(false);
  }, []);

  const login = async (credentials) => {
    try {
      console.log('🔐 AuthContext.login called with:', credentials);
      const response = await authService.login(credentials);
      console.log('🔐 AuthService response:', response);
      
      if (response.success) {
        // Combine user data and token into a single user object
        const userData = {
          ...response.data.user,
          token: response.data.token
        };
        console.log('🔐 User data to store:', userData);
        setUser(userData);
        localStorage.setItem('user', JSON.stringify(userData));
        console.log('🔐 User stored in localStorage');
        toast.success('Login successful!');
        return { success: true };
      } else {
        console.error('🔐 Login failed:', response);
        toast.error('Login failed');
        return { success: false, error: 'Login failed' };
      }
    } catch (error) {
      console.error('🔐 Login error:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Login failed. Please try again.';
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const logout = async () => {
    try {
      await authService.logout();
      setUser(null);
      localStorage.removeItem('user');
      toast.success('Logged out successfully');
    } catch (error) {
      // Even if API call fails, clear user state
      setUser(null);
      localStorage.removeItem('user');
      toast.success('Logged out successfully');
    }
  };

  const value = {
    user,
    login,
    logout,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
